import React, { FC, memo, useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { ApplicabilitySelect } from './ApplicabilitySelect';
import { WarehouseSelect } from './WarehouseSelect';
import { BoxesRevenueFilterKeys } from '../../model/types';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import dayjs, { Dayjs } from 'dayjs';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { RangeInput } from 'shared/ui/RangeInput/RangeInput';
import { Input } from 'shared/ui/Input';

export const BoxesRevenueFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<BoxesRevenueFilterKeys>();

  const [unitNameError, setUnitNameError] = useState<string | null>(null);

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: BoxesRevenueFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changeStartDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: BoxesRevenueFilterKeys.startDate,
        label: t('From {{dueDateFrom}}', { dueDateFrom: clientFormatDateFrom }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  const changeEndDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: BoxesRevenueFilterKeys.endDate,
        label: t('To {{dueDateTo}}', { dueDateTo: clientFormatDateTo }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  const changeContractApplicability = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: BoxesRevenueFilterKeys.isApplicable, label, value, title: t('Applicability') });
    },
    [changeFilters, t],
  );

  const changeSquareFrom = useCallback(
    (value: Nullable<number>) => {
      changeFilters({
        key: BoxesRevenueFilterKeys.squareFrom,
        label: t('From {{squareFrom}} m²', { squareFrom: value }),
        value,
        title: t('Square'),
      });
    },
    [changeFilters, t],
  );

  const changeSquareTo = useCallback(
    (value: Nullable<number>) => {
      changeFilters({
        key: BoxesRevenueFilterKeys.squareTo,
        label: t('To {{squareTo}} m²', { squareTo: value }),
        value,
        title: t('Square'),
      });
    },
    [changeFilters, t],
  );

  const changeUnitName = useCallback(
    (value: Nullable<string>) => {
      if (value && value.length < 2) {
        setUnitNameError(t('Unit name must be at least 2 characters long!'));
      } else {
        setUnitNameError(null);
      }

      changeFilters({ key: BoxesRevenueFilterKeys.unitName, label: `${value}`, value: value || undefined, title: t('Unit') });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Date range')}
          onChangeFrom={changeStartDate}
          onChangeTo={changeEndDate}
          values={[filters.startDate?.value && dayjs(filters.startDate?.value), filters.endDate?.value && dayjs(filters.endDate?.value)]}
        />

        <RangeInput
          values={[filters.squareFrom?.value, filters.squareTo?.value]}
          placeholder={[t('From'), t('To')]}
          label={t('Square')}
          type="number"
          onChangeFrom={changeSquareFrom}
          onChangeTo={changeSquareTo}
        />

        <div className="flex space-x-3 items-center">
          <div className="flex-1">
            <div className="mb-2">{t('Unit')}</div>
            <Input placeholder={t('Unit name')} value={filters.unitName?.value} onChange={changeUnitName} bordered />
            {unitNameError && <div className="text-error mt-1">{unitNameError}</div>}
          </div>

          <ApplicabilitySelect value={filters.isApplicable?.value} onChange={changeContractApplicability} onClear={clearCurrentFilters} />
        </div>
      </div>
    </TableFilter>
  );
});
